import React, { useState, useRef, useEffect } from 'react';
import type { FormEvent } from 'react';
import { Textarea } from '../common/Textarea';
import { useLanguage } from '../../i18n/useLanguage';
import './MessageInput.css';

interface MessageInputProps {
  onSendMessage: (content: string) => Promise<void>;
  onTyping?: () => void;
  disabled?: boolean;
  placeholder?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onTyping,
  disabled = false,
  placeholder,
}) => {
  const { t } = useLanguage();
  const resolvedPlaceholder = placeholder ?? t('messages.input.placeholder');
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [content]);

  const sendMessage = async () => {
    if (!content.trim() || isSending || disabled) return;

    setIsSending(true);
    try {
      await onSendMessage(content.trim());
      setContent('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Error handling could be improved with user feedback
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await sendMessage();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send message on Enter, but allow Shift+Enter for new line
    if (e.key === 'Enter' && !e.shiftKey && !isSending && !disabled) {
      e.preventDefault();
      sendMessage();
    }
  };

  const characterCount = content.length;
  const maxLength = 5000;

  return (
    <form className="message-input" onSubmit={handleSubmit}>
      <div className="message-input-wrapper">
        <Textarea
          ref={textareaRef as React.RefObject<HTMLTextAreaElement>}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            if (e.target.value) onTyping?.();
          }}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          disabled={disabled || isSending}
          rows={1}
          maxLength={maxLength}
          style={{ 
            maxHeight: '120px', 
            overflowY: 'auto',
            resize: 'none',
            height: 'auto'
          }}
        />
        {characterCount > maxLength * 0.9 && (
          <span className="message-input-char-count">
            {characterCount}/{maxLength}
          </span>
        )}
      </div>
      <div className="message-input-actions">
        <button
          type="submit"
          disabled={!content.trim() || isSending || disabled}
          title={content.trim() ? t('messages.input.sendTitle') : t('messages.input.sendTitleEmpty')}
          className="message-input-send-button"
        >
          {isSending ? (
            <span className="message-input-spinner">…</span>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          )}
        </button>
      </div>
    </form>
  );
};
